import React from 'react';
import { ColorValue, StyleProp, View, ViewStyle } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faBasketShopping, faReceipt } from '@fortawesome/pro-regular-svg-icons';
import useStyles from '../../../hooks/styles-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import Label, { LabelType } from '../Label';
import CustomMarkdown from '../CustomMarkDown';
import MoreInfoButton, { MoreInfoButtonProps } from '../MoreInfoButton';
import { getMissionReimbursementHelp } from '../../../helpers/missionCompensation';

export interface MissionPurchaseConditionsValue {
  requiredPurchase?: boolean;
  requiredPurchaseText?: string;
  requiredPurchaseExclusionText?: string;
  reimbursementValue?: string;
  reimbursementMaxAmount?: number;
  reimbursementMaxPoints?: number;
  totalPaymentMaxAmount?: number | null;
}

interface MissionPurchaseConditionsProps {
  conditions: MissionPurchaseConditionsValue;
  settled?: boolean;
  showReimbursementPoints?: boolean;
  textColor?: ColorValue;
  iconColor?: ColorValue;
  helpColor?: string;
  rowStyle?: StyleProp<ViewStyle>;
  moreInfoButtonProps?: Pick<MoreInfoButtonProps, 'suspendDrawerOnOpen' | 'useLightBoxOnNative' | 'onOpen'>;
}

const MissionPurchaseConditions = ({ conditions, settled = false, showReimbursementPoints = false,
  textColor, iconColor, helpColor, rowStyle, moreInfoButtonProps }: MissionPurchaseConditionsProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const uiLanguage = useAppSelector(state => state.userPrefs.uiLanguage);
  const styles = useStyles(section => section.missionDetailCard);
  const row = styles.missionsBranchDetail.content.infoContainer;
  const currency = (amount: number) => new Intl.NumberFormat(uiLanguage?.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    style: 'currency', currency: 'CAD',
  }).format(amount);
  const help = getMissionReimbursementHelp(conditions.reimbursementValue ?? '', conditions.reimbursementMaxAmount, uiLanguage);
  const helpText = t(help.key, help.values);
  const labelColor = textColor ? { color: textColor } : {};

  return <>
    {conditions.requiredPurchase && <View style={[row.summaryItem, rowStyle]}>
      <FontAwesomeIcon icon={faBasketShopping} color={(iconColor ?? theme.text.primary).toString()} size={20} style={row.summaryItemIcon} />
      <Label numberOfLines={0} variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flex: 1, fontWeight: '700', ...labelColor }}>
        {t('purchase_required')}
      </Label>
    </View>}
    {!!conditions.reimbursementValue && <View style={[row.summaryItem, rowStyle]}>
      <FontAwesomeIcon icon={faReceipt} color={(iconColor ?? theme.text.primary).toString()} size={20} style={row.summaryItemIcon} />
      <View style={[row.summaryItemValueSecondary, { flex: 1, minWidth: 0, alignItems: 'center' }]}>
        <Label numberOfLines={0} variant={LabelType.valueLargeMission} style={{ marginLeft: 6, flexShrink: 1, ...labelColor }}>
          {conditions.reimbursementValue}
        </Label>
        <View style={{ flex: 1, minWidth: 0, marginLeft: 12 }}>
          <Label numberOfLines={0} variant={LabelType.titleDarkSmall} style={{ fontWeight: '700', ...labelColor }}>
            {settled ? t('reimbursement') : `${t('reimbursement-up-to')} ${conditions.reimbursementValue}`}
          </Label>
          {!settled && conditions.reimbursementMaxAmount > 0 && <Label numberOfLines={0} variant={LabelType.titleDarkSmall} style={labelColor}>
            {t('at-home-reimbursement-cap', { amount: currency(conditions.reimbursementMaxAmount) })}
            {showReimbursementPoints && conditions.reimbursementMaxPoints !== undefined ? ` (${conditions.reimbursementMaxPoints} pts)` : ''}
          </Label>}
        </View>
      </View>
      {help.visible && <MoreInfoButton {...moreInfoButtonProps} style={{ alignSelf: 'flex-start', marginLeft: 8, flexShrink: 0 }}
        title="" color={helpColor}>
        {helpText === help.key ? t(help.fallbackKey) : helpText}
      </MoreInfoButton>}
    </View>}
  </>;
};

export const MissionPurchaseDetails = ({ conditions }: { conditions: MissionPurchaseConditionsValue }) => {
  if (!conditions.requiredPurchaseText && !conditions.requiredPurchaseExclusionText) return null;
  return <View testID="mission-purchase-details" style={{ width: '100%', minWidth: 0, paddingHorizontal: 12, marginBottom: 16, gap: 8 }}>
    {!!conditions.requiredPurchaseText && <CustomMarkdown>{conditions.requiredPurchaseText}</CustomMarkdown>}
    {!!conditions.requiredPurchaseExclusionText && <CustomMarkdown>{conditions.requiredPurchaseExclusionText}</CustomMarkdown>}
  </View>;
};

export default MissionPurchaseConditions;
